/**
 * "Pro Santé Connect becomes mandatory for doctors … by …".
 *
 * Only the card has a grace window — password + TOTP are owed at once, and an
 * account missing them never reaches this screen (the wizard replaces it). A
 * window is only useful if it is said out loud: without this banner a doctor
 * would learn the rule on the day it bites. The date is the account's own (its
 * first login where Pro Santé Connect is offered, plus the window), so two readers
 * of the same screen may see two dates.
 *
 * Not dismissible, deliberately: a reminder that can be silenced once and for all
 * reminds nobody of anything. It disappears on its own the moment the account
 * carries what it owes, because it renders from the state the API serves rather
 * than from a local flag.
 */
import { ShieldAlert } from "lucide-react";
import { Trans } from "@lingui/macro";
import { Link } from "react-router";
import useUser from "@app/zustand/user";
import { formatDeadline, readAuthPolicy } from "@app/utils/auth-policy";

export default function AuthSetupBanner() {
  const user = useUser((state) => state.user);
  const policy = readAuthPolicy(user);

  if (!policy || policy.state !== "due") return null;
  if (!policy.missing_factors.includes("psc")) return null;

  const pscDeadline = formatDeadline(policy.psc_deadline);

  return (
    <div className="flex items-center justify-between gap-3 border-b border-primary/20 bg-blue-50 px-4 py-2 text-sm text-blue-900">
      <span className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span className="block">
          {pscDeadline ? (
            <Trans>
              <strong>
                Pro Santé Connect devient obligatoire pour les médecins.
              </strong>{" "}
              Associez votre carte CPS ou votre e-CPS au plus tard le{" "}
              <strong>{pscDeadline}</strong>.
            </Trans>
          ) : (
            <Trans>
              <strong>
                Pro Santé Connect devient obligatoire pour les médecins.
              </strong>{" "}
              Associez votre carte CPS ou votre e-CPS.
            </Trans>
          )}
        </span>
      </span>
      <Link
        to="/configuration?onglet=compte"
        className="whitespace-nowrap font-medium text-primary hover:text-primary/80"
      >
        <Trans>Sécuriser mon compte</Trans>
      </Link>
    </div>
  );
}
